import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { SurveillanceCameraDto } from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { PageHeader } from '../shared/ui/PageHeader';

function FullscreenCameraModal({
  camera,
  onClose,
}: {
  camera: SurveillanceCameraDto;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div className="surveillance-fs-modal" role="dialog" aria-modal="true">
      <div className="surveillance-fs-modal__bar">
        <div>
          <div className="fw-semibold">{camera.code}</div>
          <div className="small text-white-50">{camera.location}</div>
        </div>
        <button type="button" className="btn btn-light btn-sm" onClick={onClose}>
          <i className="bi bi-x-lg me-1" />
          {t('vigilancia.close')}
        </button>
      </div>
      <iframe
        className="surveillance-fs-modal__frame"
        src={camera.url}
        title={`${camera.code} — ${camera.location}`}
        allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>,
    document.body,
  );
}

export function VigilanciaPage() {
  const { t } = useTranslation();
  const canRead = useAuthStore(
    (s) => s.hasPermission('settings:read') || s.hasPermission('parkings:read'),
  );
  const [fullscreen, setFullscreen] = useState<SurveillanceCameraDto | null>(null);

  const cameras = useQuery({
    queryKey: ['surveillance-cameras', 'active'],
    queryFn: () => api.listSurveillanceCameras(true),
    enabled: canRead,
    refetchInterval: 60_000,
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('vigilancia.forbidden')}</div>;
  }

  const rows = cameras.data?.data ?? [];

  return (
    <>
      <PageHeader />

      {cameras.isError && (
        <div className="alert alert-danger">{(cameras.error as Error).message}</div>
      )}

      {cameras.isLoading && <div className="text-muted py-4">{t('vigilancia.loading')}</div>}

      {!cameras.isLoading && rows.length === 0 && (
        <div className="alert alert-light border">{t('vigilancia.empty')}</div>
      )}

      <div className="surveillance-grid">
        {rows.map((c) => (
          <article key={c.id} className="surveillance-card">
            <div className="surveillance-card__media">
              <iframe
                src={c.url}
                title={`${c.code} — ${c.location}`}
                loading="lazy"
                allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <div className="surveillance-card__body">
              <div>
                <div className="surveillance-card__code">{c.code}</div>
                <div className="surveillance-card__location">{c.location}</div>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setFullscreen(c)}
              >
                <i className="bi bi-arrows-fullscreen me-1" />
                {t('vigilancia.expand')}
              </button>
            </div>
          </article>
        ))}
      </div>

      {fullscreen && (
        <FullscreenCameraModal camera={fullscreen} onClose={() => setFullscreen(null)} />
      )}
    </>
  );
}
